import { useEffect, useRef, useState } from 'react';
import {
  formatEquation,
  type Equation,
  type AlgebraOperation,
} from '../../shared/algebra.ts';
import { TutorAvatar } from './TutorAvatar.tsx';
import type { Focus, SceneFrame } from '../lib/lessonScene.ts';
import { leftTileLayout, expressionSymbolSlots } from '../lib/boardLayout.ts';
import './animated-board-motion.css';

export function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(m.matches);
    m.addEventListener('change', update);
    return () => m.removeEventListener('change', update);
  }, []);
  return reduced;
}
const lerp = (a: number, b: number, p: number) => a + (b - a) * p;
function useCalmFocus(focus: Focus) {
  const latest = useRef(focus);
  latest.current = focus;
  const [held, setHeld] = useState(focus);
  useEffect(() => {
    const id = window.setInterval(() => setHeld(latest.current), 800);
    return () => window.clearInterval(id);
  }, []);
  return held;
}
const anchors: Record<Focus, { x: number; y: number }> = {
  none: { x: 553, y: 125 },
  x: { x: 311, y: 88 },
  coefficient: { x: 252, y: 88 },
  constant: { x: 429, y: 88 },
  total: { x: 681, y: 88 },
  equals: { x: 553, y: 88 },
  left: { x: 353, y: 113 },
  right: { x: 681, y: 113 },
  both: { x: 553, y: 125 },
  rate: { x: 200, y: 197 },
  hours: { x: 434, y: 197 },
  result: { x: 619, y: 94 },
};
function Symbol({
  id,
  text,
  x,
  y,
  focus,
  width = 65,
  cancel = false,
  small = false,
  opacity = 1,
}: {
  id: string;
  text: string;
  x: number;
  y: number;
  focus: boolean;
  width?: number;
  cancel?: boolean;
  small?: boolean;
  opacity?: number;
}) {
  return (
    <g
      data-math-id={id}
      data-active={focus || undefined}
      transform={`translate(${x} ${y})`}
      opacity={opacity}
    >
      <rect
        x={-width / 2}
        y={small ? -24 : -36}
        width={width}
        height={small ? 40 : 62}
        rx="12"
        fill={focus ? '#ffedb9' : 'transparent'}
        stroke={focus ? '#c99137' : 'transparent'}
        strokeWidth="1.4"
      />
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        className={small ? 'board-symbol small' : 'board-symbol'}
      >
        {text}
      </text>
      {focus && (
        <path
          d={`M${-width / 2 + 10} ${small ? 21 : 34}H${width / 2 - 10}`}
          stroke="#b17a22"
          strokeWidth="3"
          strokeLinecap="round"
        />
      )}
      {cancel && (
        <path
          className="cancel-stroke"
          d={`M${-width / 2} 19L${width / 2} -24`}
          stroke="#b45543"
          strokeWidth="3.5"
          strokeLinecap="round"
        />
      )}
    </g>
  );
}
function Tile({
  id,
  x,
  y,
  label = '1',
  unknown = false,
  cancel = false,
  opacity = 1,
  active = false,
}: {
  id: string;
  x: number;
  y: number;
  label?: string;
  unknown?: boolean;
  cancel?: boolean;
  opacity?: number;
  active?: boolean;
}) {
  return (
    <g data-tile-id={id} transform={`translate(${x} ${y})`} opacity={opacity}>
      <rect
        x={unknown ? -23 : -14}
        y={unknown ? -34 : -14}
        width={unknown ? 46 : 28}
        height={unknown ? 68 : 28}
        rx={unknown ? 10 : 7}
        fill={unknown ? '#dceac8' : '#f7de9e'}
        stroke={active ? '#a66b1b' : unknown ? '#799968' : '#c3a05b'}
        strokeWidth={active ? 3 : 1.5}
      />
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        className={unknown ? 'tile-letter' : 'tile-number'}
      >
        {label}
      </text>
      {cancel && (
        <path
          d={unknown ? 'M-22 29L22-29' : 'M-13 12L13-12'}
          stroke="#ac513e"
          strokeWidth="3"
          strokeLinecap="round"
        />
      )}
    </g>
  );
}

/** Every transform is derived from media time. Pause/seek/replay cannot leave a CSS timeline running. */
export function AnimatedBoard({
  frame,
  speaking = false,
  reduced = false,
  equation,
  onPoint,
  children,
  label = 'Worked example',
  operation,
  operationProgress = 0,
}: {
  frame: SceneFrame;
  speaking?: boolean;
  reduced?: boolean;
  equation?: Equation;
  onPoint?: (term: string) => void;
  children?: React.ReactNode;
  label?: string;
  operation?: AlgebraOperation;
  operationProgress?: number;
}) {
  const [compact, setCompact] = useState(
    () => window.matchMedia('(max-width: 760px)').matches,
  );
  useEffect(() => {
    const media = window.matchMedia('(max-width: 760px)');
    const change = () => setCompact(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  const { kind, phase } = frame;
  const focus =
    equation && frame.focus === 'rate'
      ? 'coefficient'
      : equation && frame.focus === 'hours'
        ? 'x'
        : frame.focus;
  const heldFocus = useCalmFocus(focus);
  const pointerFocus = frame.pointerFocus ?? heldFocus;
  const p = reduced ? 1 : frame.progress;
  const isGeneric = !!equation;
  const leftX = isGeneric
    ? equation.left.x
    : kind === 'practice'
      ? '3'
      : kind === 'divide' && phase >= 4
        ? '1'
        : '2';
  const leftConstant = isGeneric
    ? equation.left.constant
    : kind === 'practice'
      ? '6'
      : kind === 'divide' || (kind === 'subtract' && phase >= 4)
        ? '0'
        : '4';
  const total = isGeneric
    ? equation.right.constant
    : kind === 'practice'
      ? '18'
      : kind === 'subtract' && phase >= 4
        ? '6'
        : kind === 'divide'
          ? phase >= 4
            ? '3'
            : '6'
          : '10';
  const check = kind === 'check' && !isGeneric;
  const story = kind === 'story' && !isGeneric;
  const subtract = kind === 'subtract' && !isGeneric;
  const divide = kind === 'divide' && !isGeneric;
  const focusOn = (...f: Focus[]) => f.includes(focus) || focus === 'both';
  let target = { ...anchors[pointerFocus] };
  if (check && pointerFocus === 'coefficient') target = { x: 230, y: 88 };
  if (check && pointerFocus === 'x') target = { x: 300, y: 88 };
  if (story && phase < 5)
    target =
      pointerFocus === 'constant'
        ? { x: 223, y: 181 }
        : pointerFocus === 'rate'
          ? { x: 466, y: 181 }
          : pointerFocus === 'total'
            ? { x: 709, y: 181 }
            : { x: 466, y: 234 };
  if (divide && phase >= 2 && phase <= 3)
    target = phase === 2 ? { x: 350, y: 196 } : { x: 630, y: 196 };
  if (subtract && phase === 2) target = { x: 420, y: 236 };
  if (subtract && phase === 3) target = { x: 677, y: 244 };
  const avatarX = Math.max(compact ? 215 : 36, Math.min(792, target.x - 165));
  const avatarY = 274;
  const isSimple =
    !isGeneric ||
    (equation.right.x === '0' &&
      Number.isInteger(Number(leftX)) &&
      Number(leftX) >= 0 &&
      Number(leftX) <= 5 &&
      Number.isInteger(Number(leftConstant)) &&
      Number(leftConstant) >= 0 &&
      Number(leftConstant) <= 20 &&
      Number.isInteger(Number(total)) &&
      Number(total) >= 0 &&
      Number(total) <= 24);
  const unitCount = divide ? 6 : check ? 10 : Number(total);
  const xCount = divide ? 2 : Math.max(0, Math.min(5, Number(leftX)));
  const liveSubtract =
    isGeneric &&
    operation?.kind === 'subtract' &&
    Number(operation.value) > 0 &&
    operation.value === leftConstant &&
    operationProgress >= 0.18 &&
    operationProgress < 0.8;
  const liveDivide =
    isGeneric &&
    operation?.kind === 'divide' &&
    operation.value === leftX &&
    leftConstant === '0' &&
    xCount >= 2 &&
    xCount <= 3 &&
    operationProgress >= 0.18 &&
    operationProgress < 0.8 &&
    Number.isInteger(Number(total) / xCount);
  const liveCancel = liveSubtract && operationProgress >= 0.5;
  const groupSize = Number(total) / xCount;
  const groupProgress = Math.min(
    1,
    Math.max(0, (operationProgress - 0.18) / 0.35),
  );
  const tileLayout = leftTileLayout(
    xCount,
    Math.min(20, Math.max(0, subtract ? 4 : Number(leftConstant))),
  );
  return (
    <section
      className="animated-board"
      aria-label={label}
      data-scene-kind={kind}
      data-scene-phase={phase}
      data-scene-focus={focus}
    >
      <div className="board-topline">
        <span>{label}</span>
        <span className="board-live">
          {speaking ? 'Follow my pointer' : 'Pause, inspect, or try an idea'}
        </span>
      </div>
      <svg
        className="algebra-canvas"
        viewBox={
          compact
            ? story
              ? '115 15 710 385'
              : '210 15 565 385'
            : '0 0 960 420'
        }
        role="img"
        aria-label={`${frame.equation}. ${frame.caption}`}
      >
        <defs>
          <pattern
            id="workshop-dots"
            width="24"
            height="24"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="1" cy="1" r=".8" fill="#75907b" opacity=".18" />
          </pattern>
        </defs>
        <rect width="960" height="420" fill="url(#workshop-dots)" />
        <path d="M205 123H760" stroke="#cdd7c7" strokeWidth="1" />
        {!isSimple ? (
          <text x="480" y="89" textAnchor="middle" className="board-symbol">
            {formatEquation(equation!)}
          </text>
        ) : check ? (
          <>
            {phase < 3 ? (
              <>
                <Symbol
                  id="coefficient"
                  text="2"
                  x={230}
                  y={83}
                  width={40}
                  focus={focusOn('left', 'coefficient')}
                />
                <Symbol
                  id="x"
                  text={phase >= 1 ? '× 3' : 'x'}
                  width={phase >= 1 ? 96 : 44}
                  x={300}
                  y={83}
                  focus={focusOn('x', 'left')}
                />
              </>
            ) : (
              <Symbol
                id="evaluated-product"
                text={phase >= 4 ? '10' : '6'}
                width={94}
                x={330}
                y={83}
                focus={focusOn('left', 'equals')}
              />
            )}
            {phase < 4 && (
              <>
                <Symbol
                  id="plus"
                  text="+"
                  x={expressionSymbolSlots.plus.x}
                  y={83}
                  focus={false}
                  width={expressionSymbolSlots.plus.width}
                />
                <Symbol
                  id="constant"
                  text="4"
                  x={expressionSymbolSlots.constant.x}
                  width={expressionSymbolSlots.constant.width}
                  y={83}
                  focus={focusOn('constant')}
                />
              </>
            )}
            <Symbol
              id="equals"
              text="="
              x={553}
              y={83}
              focus={focusOn('equals')}
            />
            <Symbol
              id="total"
              text="10"
              x={681}
              y={83}
              focus={focusOn('total', 'right', 'equals')}
              width={90}
            />
          </>
        ) : (
          <>
            {leftX === '0' && leftConstant === '0' && (
              <Symbol
                id="zero-left"
                text="0"
                x={330}
                y={83}
                focus={focusOn('left')}
              />
            )}
            {leftX !== '0' && (
              <>
                <Symbol
                  id="coefficient"
                  text={leftX === '1' ? '' : leftX}
                  x={252}
                  y={83}
                  width={44}
                  focus={focusOn('coefficient', 'left')}
                />
                <Symbol
                  id="x"
                  text="x"
                  x={311}
                  y={83}
                  width={44}
                  focus={focusOn('x', 'left')}
                />
              </>
            )}
            {leftConstant !== '0' && (
              <>
                <Symbol
                  id="plus"
                  text={leftX === '0' ? '' : '+'}
                  x={expressionSymbolSlots.plus.x}
                  y={83}
                  focus={false}
                  width={expressionSymbolSlots.plus.width}
                />
                <Symbol
                  id="constant"
                  text={leftConstant}
                  x={expressionSymbolSlots.constant.x}
                  width={expressionSymbolSlots.constant.width}
                  y={83}
                  focus={focusOn('constant', 'left')}
                  cancel={(subtract && phase >= 2 && phase < 4) || liveCancel}
                />
              </>
            )}
            <Symbol
              id="equals"
              text="="
              x={553}
              y={83}
              focus={focusOn('equals')}
            />
            <Symbol
              id="total"
              text={total}
              x={681}
              y={83}
              focus={focusOn('total', 'right', 'result')}
              width={96}
            />
          </>
        )}
        {subtract && phase >= 1 && phase < 4 && (
          <>
            <Symbol
              id="subtract-left"
              text="−4"
              x={468}
              y={lerp(48, 134, phase === 1 ? p : 1)}
              small
              width={65}
              focus={focusOn('both', 'constant')}
              cancel={phase >= 2}
            />
            <Symbol
              id="subtract-right"
              text="−4"
              x={732}
              y={lerp(48, 134, phase === 1 ? p : 1)}
              small
              width={65}
              focus={focusOn('both', 'total')}
            />
          </>
        )}
        {divide && phase >= 1 && phase < 4 && (
          <g opacity={phase === 1 ? p : 1}>
            <path
              d="M230 111H344M645 111H719"
              stroke="#34554e"
              strokeWidth="2"
            />
            <Symbol
              id="divide-left"
              text="2"
              x={287}
              y={137}
              small
              focus={focusOn('both', 'left')}
            />
            <Symbol
              id="divide-right"
              text="2"
              x={681}
              y={137}
              small
              focus={focusOn('both', 'right')}
            />
          </g>
        )}
        {liveSubtract && (
          <>
            <Symbol
              id="live-subtract-left"
              text={`−${operation!.value}`}
              x={465}
              y={137}
              small
              focus={focusOn('both', 'left')}
              cancel={liveCancel}
            />
            <Symbol
              id="live-subtract-right"
              text={`−${operation!.value}`}
              x={714}
              y={137}
              small
              focus={focusOn('both', 'right')}
            />
          </>
        )}
        {liveDivide && (
          <g>
            <path
              d="M225 112H342M645 112H719"
              stroke="#34554e"
              strokeWidth="2"
            />
            <Symbol
              id="live-divide-left"
              text={operation!.value}
              x={285}
              y={138}
              small
              focus={focusOn('left', 'both')}
            />
            <Symbol
              id="live-divide-right"
              text={operation!.value}
              x={681}
              y={138}
              small
              focus={focusOn('right', 'both')}
            />
          </g>
        )}
        {story ? (
          <>
            {[
              { x: 223, a: '$4', b: 'starting charge', show: 1 },
              { x: 466, a: '$2 × x', b: 'rate × hours', show: 2 },
              { x: 709, a: '$10', b: 'receipt total', show: 3 },
            ].map((c) => (
              <g
                key={c.b}
                transform={`translate(${phase >= 5 ? lerp(c.x, c.show === 1 ? 429 : c.show === 2 ? 300 : 681, phase === 5 ? p : 1) : c.x} ${phase >= 5 ? lerp(205, 165, phase === 5 ? p : 1) : 205})`}
                opacity={phase >= c.show ? 1 : 0.24}
              >
                <rect
                  x="-98"
                  y="-49"
                  width="196"
                  height="90"
                  rx="16"
                  fill="#fffdf4"
                  stroke="#9caf8e"
                />
                <text textAnchor="middle" y="-10" className="story-value">
                  {c.a}
                </text>
                <text textAnchor="middle" y="20" className="board-note">
                  {c.b}
                </text>
              </g>
            ))}
            {phase >= 4 && (
              <text x="480" y="275" className="board-note" textAnchor="middle">
                {phase >= 6
                  ? 'x = 3 hours → $2 × 3 + $4 = $10'
                  : 'x means the number of hours rented'}
              </text>
            )}
          </>
        ) : isSimple ? (
          <>
            {divide && phase >= 1 && (
              <g
                opacity={phase === 1 ? p : 1}
                fill="none"
                stroke="#9caf8e"
                strokeDasharray="6 5"
              >
                <rect x="304" y="158" width="424" height="77" rx="17" />
                <rect x="304" y="247" width="424" height="77" rx="17" />
              </g>
            )}
            {Array.from({ length: xCount }, (_, i) => {
              const split = divide && phase >= 1;
              const intro = kind === 'intro' && !isGeneric;
              const home = tileLayout.unknowns[i];
              const x = split
                ? lerp(home.x, 350, phase === 1 ? p : 1)
                : intro && phase <= 2
                  ? lerp(311, home.x, phase === 2 ? p : 0)
                  : home.x;
              const y = split
                ? lerp(home.y, 196 + i * 89, phase === 1 ? p : 1)
                : intro && phase <= 2
                  ? lerp(128, home.y, phase === 2 ? p : 0)
                  : home.y;
              return (
                <Tile
                  key={`x-${i}`}
                  id={`x-${i}`}
                  x={liveDivide ? lerp(x, 350, groupProgress) : x}
                  y={liveDivide ? lerp(y, 185 + i * 65, groupProgress) : y}
                  unknown
                  label={check && phase >= 1 ? '3' : 'x'}
                  active={focusOn('x', 'left', 'coefficient')}
                  opacity={
                    intro && phase < 2
                      ? 0.12
                      : divide && phase >= 4 && i === 1
                        ? 0.15
                        : 1
                  }
                />
              );
            })}
            {!divide &&
              Array.from(
                {
                  length: Math.min(
                    20,
                    Math.max(0, subtract ? 4 : Number(leftConstant)),
                  ),
                },
                (_, i) => {
                  const fade =
                    subtract && phase >= 2
                      ? phase === 2
                        ? 1 - p * 0.8
                        : 0.12
                      : 1;
                  const intro = kind === 'intro' && !isGeneric;
                  const home = tileLayout.units[i];
                  return (
                    <Tile
                      key={`extra-${i}`}
                      id={`extra-${i}`}
                      x={intro && phase === 3 ? lerp(429, home.x, p) : home.x}
                      y={intro && phase === 3 ? lerp(129, home.y, p) : home.y}
                      cancel={(subtract && phase >= 2) || liveCancel}
                      active={focusOn('constant', 'left')}
                      opacity={
                        liveCancel ? 0.2 : intro && phase < 3 ? 0.12 : fade
                      }
                    />
                  );
                },
              )}
            {subtract &&
              phase >= 1 &&
              phase < 4 &&
              Array.from({ length: 4 }, (_, i) => (
                <Tile
                  key={`negative-${i}`}
                  id={`negative-${i}`}
                  x={412 + (i % 3) * 34}
                  y={lerp(
                    310,
                    265 + Math.floor(i / 3) * 35,
                    phase === 1 ? p : 1,
                  )}
                  label="−1"
                  cancel={phase >= 2}
                  opacity={phase >= 2 ? 0.22 : 1}
                />
              ))}
            {Array.from(
              { length: Math.min(24, Math.max(0, unitCount)) },
              (_, i) => {
                const split = divide && phase >= 1;
                const removed =
                  (subtract && phase >= 3 && i >= 6) ||
                  (liveCancel && i >= Number(total) - Number(operation!.value));
                const x = split
                  ? lerp(
                      601 + (i % 3) * 34,
                      606 + (i % 3) * 34,
                      phase === 1 ? p : 1,
                    )
                  : 600 + (i % 5) * 34;
                const y = split
                  ? lerp(
                      183 + Math.floor(i / 3) * 36,
                      196 + Math.floor(i / 3) * 89,
                      phase === 1 ? p : 1,
                    )
                  : 183 + Math.floor(i / 5) * 36;
                return (
                  <Tile
                    key={`right-${i}`}
                    id={`right-${i}`}
                    x={
                      liveDivide
                        ? lerp(x, 590 + (i % groupSize) * 29, groupProgress)
                        : x
                    }
                    y={
                      liveDivide
                        ? lerp(
                            y,
                            185 + Math.floor(i / groupSize) * 65,
                            groupProgress,
                          )
                        : y
                    }
                    cancel={removed}
                    opacity={
                      removed
                        ? phase === 3
                          ? 1 - p * 0.9
                          : 0.1
                        : divide && phase >= 4 && i >= 3
                          ? 0.15
                          : 1
                    }
                    active={focusOn('total', 'right', 'result')}
                  />
                );
              },
            )}
            {check && phase >= 1 && (
              <g opacity={phase === 1 ? p : 1}>
                <path d="M277 251H369" stroke="#94a67f" strokeWidth="2" />
                <text
                  x="323"
                  y="275"
                  textAnchor="middle"
                  className="board-note"
                >
                  {phase >= 2 ? '3 + 3 = 6' : 'x → 3 in each group'}
                </text>
              </g>
            )}
          </>
        ) : (
          <text x="480" y="210" textAnchor="middle" className="board-note">
            Every operation applies to both entire expressions.
          </text>
        )}
        {divide && phase >= 4 && (
          <text x="600" y="330" className="board-note" textAnchor="middle">
            Each equal group contains 3.
          </text>
        )}
        <g className="board-pointer" aria-hidden="true">
          <g opacity=".58">
            <path
              d={`M${avatarX + 21} ${avatarY + 49}L${target.x} ${target.y + 14}`}
              stroke="#f8f0d7"
              strokeWidth="8"
              strokeLinecap="round"
            />
            <path
              d={`M${avatarX + 21} ${avatarY + 49}L${target.x} ${target.y + 14}`}
              stroke="#967749"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <circle cx={target.x} cy={target.y + 14} r="5" fill="#c58c2d" />
          </g>
          <svg
            x={avatarX}
            y={avatarY}
            width="112"
            height="120"
            overflow="visible"
          >
            <TutorAvatar speaking={speaking} pointing />
          </svg>
        </g>
        {onPoint &&
          (['x', 'constant', 'total', 'equals'] as Focus[]).map((f) => (
            <g
              key={f}
              role="button"
              tabIndex={0}
              aria-label={`Ask about ${f === 'x' ? 'the unknown x' : f === 'constant' ? 'the added number' : f === 'total' ? 'the right side' : 'equality'}`}
              onClick={() => onPoint(f)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPoint(f);
                }
              }}
            >
              <rect
                x={anchors[f].x - 35}
                y="46"
                width="70"
                height="69"
                rx="12"
                fill="transparent"
                className="board-hit"
              />
            </g>
          ))}
      </svg>
      <p className="board-explanation" aria-live="off">
        <span className="board-step-marker" />
        {frame.caption}
      </p>
      {children}
    </section>
  );
}
